import { useMemo } from "react";
import { useStore } from "@/stores/useStore";
import { CARD_FIELD_OPTIONS } from "@/types/v2/Shop";

export interface UseCardFieldLabelsResult {
  getPrimaryFieldLabel: () => string;
  getSecondaryFieldLabel: () => string;
  getTertiaryFieldLabel: () => string;
  getFieldLabel: (fieldKey: "primary" | "secondary" | "tertiary") => string;
  hasCustomLabels: boolean;
}

/**
 * Hook for resolving field labels from shop configuration
 * Maps the configured field names (e.g., "RO Number", "Customer Name") to display labels
 * Falls back to default labels when field names are not configured
 */
export const useCardFieldLabels = (): UseCardFieldLabelsResult => {
  const cardContents = useStore((state) => state.cardContents);

  const fieldLabels = useMemo(() => {
    const primary = cardContents?.primaryField || "";
    const secondary = cardContents?.secondaryField || "";
    const tertiary = cardContents?.tertiaryField || "";

    return {
      primary:
        primary && CARD_FIELD_OPTIONS.includes(primary as any)
          ? primary
          : "Primary",
      secondary:
        secondary && CARD_FIELD_OPTIONS.includes(secondary as any)
          ? secondary
          : "Secondary",
      tertiary:
        tertiary && CARD_FIELD_OPTIONS.includes(tertiary as any)
          ? tertiary
          : "Tertiary",
    };
  }, [
    cardContents?.primaryField,
    cardContents?.secondaryField,
    cardContents?.tertiaryField,
  ]);

  const hasCustomLabels = useMemo(() => {
    return (
      fieldLabels.primary !== "Primary" ||
      fieldLabels.secondary !== "Secondary" ||
      fieldLabels.tertiary !== "Tertiary"
    );
  }, [fieldLabels]);

  const getPrimaryFieldLabel = () => fieldLabels.primary;
  const getSecondaryFieldLabel = () => fieldLabels.secondary;
  const getTertiaryFieldLabel = () => fieldLabels.tertiary;

  const getFieldLabel = (fieldKey: "primary" | "secondary" | "tertiary") => {
    switch (fieldKey) {
      case "primary":
        return fieldLabels.primary;
      case "secondary":
        return fieldLabels.secondary;
      case "tertiary":
        return fieldLabels.tertiary;
      default:
        return "";
    }
  };

  return {
    getPrimaryFieldLabel,
    getSecondaryFieldLabel,
    getTertiaryFieldLabel,
    getFieldLabel,
    hasCustomLabels,
  };
};
